import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Trophy } from 'lucide-react';
import { playApplause } from './applause';
import { FONT, MONO } from './ui';

const COLORS = ['#fcd34d', '#60a5fa', '#34d399', '#f472b6', '#ffffff'];
const STEPS = [{ i: 1, h: 'h-40', c: 'from-slate-300 to-slate-500', d: 400 }, { i: 0, h: 'h-56', c: 'from-amber-200 to-amber-500', d: 900 }, { i: 2, h: 'h-28', c: 'from-orange-300 to-orange-600', d: 0 }];

const Confetti = () => {
  const bits = useMemo(() => Array.from({ length: 90 }, (_, k) => ({
    k, left: Math.random() * 100, delay: Math.random() * 2.5, dur: 2.8 + Math.random() * 2.5, color: COLORS[k % COLORS.length], rot: Math.random() * 360,
  })), []);
  return bits.map((b) => (
    <span key={b.k} className="absolute top-0 w-2 h-3.5 rounded-sm" aria-hidden="true"
      style={{ left: `${b.left}%`, background: b.color, transform: `rotate(${b.rot}deg)`, animation: `vfConfetti ${b.dur}s linear ${b.delay}s infinite` }} />
  ));
};

// Projector winner screen: top 3 on a podium, confetti and applause.
export const Podium = ({ rows, title, t, onClose }) => {
  const close = useRef(onClose);
  close.current = onClose;
  const [top] = useState(rows);
  useEffect(() => {
    playApplause(4.5);
    const esc = (e) => { if (e.key === 'Escape') close.current(); };
    window.addEventListener('keydown', esc);
    return () => window.removeEventListener('keydown', esc);
  }, []);
  return createPortal(
    <div className="fixed inset-0 z-[300] bg-[#05091a] overflow-hidden flex flex-col items-center justify-center p-6" data-testid="podium-screen">
      <Confetti />
      <button onClick={onClose} className="absolute top-5 right-5 p-3 rounded-full bg-white/10 hover:bg-white/20 text-white" aria-label="close" data-testid="podium-close"><X className="w-6 h-6" /></button>
      <Trophy className="w-14 h-14 text-amber-300" />
      <p className="mt-3 text-[11px] uppercase tracking-[0.3em] text-amber-300 font-bold" style={MONO}>{t.podiumEyebrow}</p>
      <h2 className="mt-2 text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white text-center" style={FONT}>{title}</h2>
      <div className="mt-12 flex items-end gap-3 sm:gap-6">
        {STEPS.map(({ i, h, c, d }) => top[i] && (
          <div key={i} className="flex flex-col items-center w-28 sm:w-44" style={{ animation: `vfRise 0.8s cubic-bezier(.2,.9,.3,1.2) ${d}ms both` }} data-testid={`podium-place-${i + 1}`}>
            <p className="text-xl sm:text-3xl font-extrabold text-white text-center truncate max-w-full" style={FONT}>{top[i].name}</p>
            <p className="mt-1 text-sm sm:text-lg text-emerald-300 font-bold" style={MONO}>{top[i].score}/100</p>
            <div className={`mt-3 w-full ${h} rounded-t-2xl bg-gradient-to-b ${c} flex items-start justify-center pt-4`}>
              <span className="text-5xl sm:text-6xl font-extrabold text-slate-950/80" style={FONT}>{i + 1}</span>
            </div>
          </div>
        ))}
      </div>
    </div>,
    document.fullscreenElement || document.body,
  );
};
